<template>
    <div
        class="w-full rounded-lg flex flex-col justify-center items-center p-8 mt-6"
    >
        <HeaderIcon>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="#5F6D7E" style="opacity:1;"><path  d="M12 3c5.392 0 9.878 3.88 10.819 9c-.94 5.12-5.427 9-10.819 9s-9.878-3.88-10.818-9C2.122 6.88 6.608 3 12 3m0 16a9.005 9.005 0 0 0 8.778-7a9.005 9.005 0 0 0-17.555 0A9.005 9.005 0 0 0 12 19m0-2.5a4.5 4.5 0 1 1 0-9a4.5 4.5 0 0 1 0 9m0-2a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5"/></svg>
        </HeaderIcon>

        <div class="w-full max-w-lg flex flex-col justify-center items-center mt-8 z-50">
            <Intro :heading-text="headingText" :sub-text="subText" />
        </div>

        <div class="w-full max-w-2xl xl:max-w-5xl flex flex-col justify-center items-center mt-8 z-50">
            <SetupReviewCard
                v-if="formData.payload"
                :setup-data="formData.payload"
                :is-standard-plan="isStandardPlan"
                @change-plan="changePlan"
                @change-contact-info="changeContactInfo"
                @change-hubs="changeHubs"
            />
        </div>

        <div class="w-full max-w-2xl xl:max-w-5xl flex flex-col justify-start items-start gap-2 mt-8 z-50">
            <div class="text-xl font-medium text-primary-font">
                Choose Your Plan
            </div>

            <div class="w-full flex flex-col xl:flex-row justify-between items-center gap-2">

                <AccessTypeCard
                    v-for="accessType in accessTypes"
                    :key="accessType.title"
                    :title="accessType.title"
                    :description="accessType.description"
                    :recommended="accessType.recommended"
                    :selected="formData.payload.access_type === accessType.value"
                    @click.stop="handleAccessType(accessType.value)"
                />

            </div>
        </div>

        <GenericDialog v-model="paymentDialog" class="sm:max-w-lg">
            <template #title>
                <div class="text-lg font-semibold text-primary-font">
                    Payment
                </div>
            </template>
            <template #body>
                <div>
                    <Input
                        v-model="formData.payload.phone"
                        type="tel"
                        label="Phone"
                        placeholder="Phone"
                        :disabled="true"
                    />
                </div>
            </template>
            <template #footer>
                <div class="w-full flex flex-col justify-center items-center pt-4">
                    <Button @click="processKePayment" class="text-white text-sm font-semibold bg-primary-green" :disabled="isLoading">
                        <CreditCard class="w-4 h-4" />
                        <span v-if="isLoading">Processing...</span>
                        <span v-else>
                            Pay
                        </span>
                    </Button>
                </div>
            </template>
        </GenericDialog>

        <div class="w-full max-w-2xl xl:max-w-5xl flex flex-row justify-center items-center gap-4 mt-8">
            <Button
                variant="ghost"
                :disabled="isLoading"
                @click="goBack"
                class="w-full text-primary-gray bg-white border border-primary-border"
            >
                <span class="font-medium">Back</span>
            </Button>
            <Button
                variant="primary"
                @click="handleCompleteSetup"
                :disabled="isLoading"
                class="w-full py-2 px-4 bg-primary-green text-white font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-primary-green"
            >
                <span v-if="isLoading">Processing...</span>
                <span v-else>Next</span>
            </Button>
        </div>

        <Dialog v-model:open="showEmailPrompt">
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Email Address Required</DialogTitle>
                    <DialogDescription>
                        Please provide an email address to continue your payment.
                    </DialogDescription>
                </DialogHeader>
                <div class="flex items-center space-x-2 py-4">
                    <div class="grid flex-1 gap-2">
                        <label for="temp_email" class="sr-only">Email</label>
                        <Input
                            id="temp_email"
                            placeholder="example@email.com"
                            v-model="temporaryEmail"
                            type="email"
                        />
                    </div>
                </div>
                <DialogFooter class="sm:justify-start">
                    <Button type="button" variant="primary" @click="confirmEmailAndPay" :disabled="!isEmailValid">
                        Continue to Payment
                    </Button>
                    <DialogClose as-child>
                        <Button type="button" variant="ghost">
                            Cancel
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>

<script setup>
import { onMounted, computed, ref } from "vue";
import { useSignupWizardStore } from "@/store";
import { storeToRefs } from "pinia";
import HeaderIcon from "@/components/auth/components/HeaderIcon.vue";
import Intro from "@/components/auth/components/Intro.vue";
import SetupReviewCard from "@/components/auth/components/SetupReviewCard.vue";
import AccessTypeCard from "./components/AccessTypeCard.vue";
import Button from "@/components/ui/button/Button.vue";
import { useNotification, useConfig } from "@/composables";
import Paystack from '@paystack/inline-js';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
} from "@/components/ui/dialog";
import Input from "@/components/ui/input/Input.vue";
import { CreditCard } from "lucide-vue-next";
import GenericDialog from "@/components/ui/dialog/GenericDialog.vue";

const popup = new Paystack()

const signupWizardStore = useSignupWizardStore();
const {
    formData,
    isLoading,
    isStandardPlan,
    subscriptionPlanPrice,
    subscriptionPlanInterval,
    isKeLocale,
    subscriptionPlanPaystackPlanCode
} = storeToRefs(signupWizardStore);
const {
    fetchSignupState,
    selectAccessType,
    goBack,
    completeSetup,
    setTransaction,
    handlePrevious,
    processKePayment,
    makeMpesaPaymentRequest
} = signupWizardStore;

const { showError } = useNotification();
const { paystackPublicKey } = useConfig();

const showEmailPrompt = ref(false);
const temporaryEmail = ref("");
const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

const isEmailValid = computed(() => {
    return emailPattern.test(temporaryEmail.value);
});

const headingText = "Review your selection";
const subText =
"Take a moment to check everything. You can change your setup now or later with help from our team.";

const accessTypes = [
    {
        title: "Pay Now",
        description: "Full feature access immediately",
        recommended: true,
        value: 'paid'
    },
    {
        title: "Continue with Limited Access",
        description: "Core features only",
        recommended: false,
        value: 'free'
    }
]

const paymentDialog = ref(false);
const setPaymentDialog = (value) => {
    paymentDialog.value = value;
}

const changePlan = () => {
    handlePrevious(5);
}

const changeContactInfo = () => {
    handlePrevious(3);
}

const changeHubs = () => {
    handlePrevious(6);
}

const reference = computed(() => {
    let text = "";
    let possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

    for (let i = 0; i < 10; i++)
    text += possible.charAt(Math.floor(Math.random() * possible.length));

    return text;
})

const processPayment = (accessType) => {
    if(isKeLocale.value){
        setPaymentDialog(true);
    }else{
        popup.newTransaction({
            key: paystackPublicKey,
            firstName: formData.value.payload.first_name,
            lastName: formData.value.payload.last_name,
            email: formData.value.payload.email,
            phone: formData.value.payload.phone,
            amount: subscriptionPlanPrice.value * 100,
            reference: reference.value,
            plan_code: subscriptionPlanPaystackPlanCode.value,
            onSuccess: (transaction) => {
                setTransaction(transaction);
                completeSetup();
            },
            onLoad: (response) => {
                console.log("onLoad: ", response);
            },
            onCancel: () => {
                console.log("onCancel");
            },
            onError: (error) => {
                console.log("Error: ", error.message);
                showError('Payment failed');
            }
        })
    }
}

const handleAccessType = (accessType) => {
    selectAccessType(accessType)
}

const handleCompleteSetup = () => {
    if(formData.value?.payload?.access_type === 'paid') {
        if (!formData.value.payload.email && !isKeLocale.value) {
            showEmailPrompt.value = true;
            return;
        }
        processPayment(formData.value?.payload?.access_type);
    } else {
        completeSetup();
    }
}

const confirmEmailAndPay = () => {
    if (!isEmailValid.value) return;

    // Update the store with the temporary email
    signupWizardStore.setFormData({
        payload: {
            email: temporaryEmail.value
        }
    });

    showEmailPrompt.value = false;
    processPayment('paid');
}

onMounted(async () => {
    await fetchSignupState();
});
</script>
